import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import { useCurrency } from "@/components/Price";
import Logo from "@/components/Logo";
import BrandToolbar from "@/components/BrandToolbar";
import DashboardLayout from "@/pages/DashboardLayout";
import PaymentSuccessBanner from "@/components/PaymentSuccessBanner";
import { SHOW_FOUNDERS_PROGRAM } from "@/config/features";
import { Check, Sparkles, ArrowLeft, Loader2, Flame, Tag, X, Lock } from "lucide-react";
import { getPlanFeatureKeys } from "@/config/planFeatures";
import { isNative, WEB_BILLING_URL } from "@/lib/platform";

function PublicPlansShell({ children }) {
  const { t } = useI18n();
  return (
    <div className="bg-obsidian text-white min-h-screen">
      <nav className="glass-header fixed top-0 left-0 right-0 z-50 pt-safe">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-5 flex items-center justify-between gap-2">
          <Link to="/" data-testid="public-plans-home"><Logo size={24} className="sm:!gap-3 shrink-0" /></Link>
          <div className="flex items-center gap-1.5 sm:gap-4 shrink-0">
            <BrandToolbar />
            <Link
              to="/login"
              className="btn-ghost px-2.5 sm:px-6 py-1.5 sm:py-2.5 text-xs sm:text-sm whitespace-nowrap"
              data-testid="public-plans-login"
            >
              {t("nav_login")}
            </Link>
            <Link
              to="/login?signup=1"
              className="btn-gold px-2.5 sm:px-5 py-1.5 sm:py-2.5 text-xs sm:text-sm whitespace-nowrap"
              data-testid="public-plans-signup"
            >
              {t("cta_secure_legacy")}
            </Link>
          </div>
        </div>
      </nav>
      <div className="pt-28 sm:pt-32 pb-16 px-4 sm:px-8 max-w-7xl mx-auto">
        {children}
      </div>
    </div>
  );
}

function PlansContent() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { symbol } = useCurrency();
  const [busy, setBusy] = useState(null);
  const [slots, setSlots] = useState({ used: 0, total: 500, available: 500, is_available: true });
  const [foundersSlots, setFoundersSlots] = useState({ used: 0, total: 500, available: 500, is_available: true });
  // Discount code state — applies to ALL plans in this view
  const [discountInput, setDiscountInput] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);  // {code, percent, is_full, plan}
  const [discountError, setDiscountError] = useState("");
  const [validating, setValidating] = useState(false);

  // Post-confirmation welcome flow — triggered by /plans?welcome=1
  const isWelcomeMode = (() => {
    try { return new URLSearchParams(window.location.search).get("welcome") === "1"; }
    catch { return false; }
  })();

  // Win-back offer trigger (Iter 23). When a freshly-confirmed user in welcome
  // mode leaves without applying a discount or purchasing, the backend sends
  // them a personal 25%-off code valid for 24h. The endpoint is idempotent
  // and skips users who already paid/used a discount, so it's safe to fire
  // optimistically from both the explicit Skip click and the unmount cleanup.
  const offerFiredRef = useRef(false);
  const fireWelcomeOffer = (useBeacon = false) => {
    if (offerFiredRef.current) return;
    if (!isWelcomeMode) return;
    if (!user || user.plan !== "free") return;
    if (discountApplied) return;  // user already applied a code in this view
    offerFiredRef.current = true;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/auth/welcome-offer-skip`;
    if (useBeacon && navigator.sendBeacon) {
      try {
        const blob = new Blob([JSON.stringify({})], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      } catch (_) { /* fall through to fetch */ }
    }
    // Fire-and-forget; failure shouldn't block navigation
    api.post("/auth/welcome-offer-skip").catch(() => {});
  };

  // Cleanup: send the offer when the user navigates away from /plans?welcome=1
  // without paying or applying a discount. Also covers tab-close via beforeunload.
  useEffect(() => {
    if (!isWelcomeMode) return;
    const onBeforeUnload = () => fireWelcomeOffer(true);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      // Component unmount (in-app navigation) — try a normal fetch first
      fireWelcomeOffer(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcomeMode, user?.plan, discountApplied?.code]);

  useEffect(() => {
    api.get("/lifetime-slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/founders-slots").then((r) => setFoundersSlots(r.data)).catch(() => {});
  }, []);

  // Auto-apply ?code=XXXX coming from the welcome-offer email deep-link.
  // Behaviour:
  //   - Always pre-fills the input so the user sees the code immediately.
  //   - If authenticated → validates and strips the URL on success.
  //   - If NOT authenticated → keeps the URL with ?code= so that after
  //     login (Login.jsx returns to /plans for free users) the effect re-fires
  //     and auto-applies. Runs at most once per mount.
  const autoCodeAppliedRef = useRef(false);
  useEffect(() => {
    if (autoCodeAppliedRef.current) return;
    let code;
    try { code = new URLSearchParams(window.location.search).get("code"); }
    catch { code = null; }
    if (!code) return;
    code = code.trim().toUpperCase();
    // Pre-fill the visible input regardless of auth so the user can see it.
    setDiscountInput(code);
    if (!user) return;  // wait until the user logs in — the effect re-fires
    autoCodeAppliedRef.current = true;
    applyDiscount(code).then((ok) => {
      // Only strip the URL on a successful apply. Keeping ?code= on failure
      // lets the user retry from the same link without losing the code.
      if (!ok) return;
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("code");
        const qs = u.searchParams.toString();
        window.history.replaceState({}, "", u.pathname + (qs ? `?${qs}` : ""));
      } catch (_) { /* noop */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const applyDiscount = async (codeOverride = null) => {
    const raw = (codeOverride ?? discountInput).trim().toUpperCase();
    if (!raw) return setDiscountError(t("plans_discount_invalid_short"));
    setValidating(true);
    setDiscountError("");
    // Keep the input in sync when called from the URL deep-link
    if (codeOverride && discountInput !== raw) setDiscountInput(raw);
    // Try the plans LIFETIME-first so a 100% lifetime code (e.g. BETA100)
    // is auto-claimed even if the user impulsively clicks "Start Premium"
    // before the 500ms toast delay finishes. The previous order
    // (personal → personal_annual → lifetime) opened a small UX window
    // where the user could trigger a personal checkout with a
    // lifetime-only code, get a 400 error, and feel like nothing worked.
    const tryPlans = ["lifetime", "personal_annual", "personal"];
    let last = null;
    for (const p of tryPlans) {
      try {
        const r = await api.post("/discount-codes/validate", {
          code: raw,
          plan: p,
        });
        setDiscountApplied({ ...r.data, accepted_plans: [], plan: p });
        toast.success(t("plans_discount_success_short"));
        setValidating(false);
        // 100% discount path (Bug 1 fix — Iter 26): auto-claim the matched
        // plan so the user doesn't have to click a second button to actually
        // unlock the features. Without this, applying BETA100 just showed
        // the "Applied" banner and the user thought they were unlocked,
        // but their plan stayed "free" and the paywall kept blocking.
        if (user && r.data?.is_full) {
          // Small delay so the success toast is visible before the redirect.
          // Pass the code explicitly to avoid a React state race where
          // `discountApplied` may not be updated by the time `upgrade` runs.
          setTimeout(() => upgrade(p, raw), 500);
        }
        return true;
      } catch (err) {
        last = err;
      }
    }
    setDiscountApplied(null);
    setDiscountError(last?.response?.data?.detail || t("plans_discount_invalid_short"));
    setValidating(false);
    return false;
  };

  const clearDiscount = () => {
    setDiscountApplied(null);
    setDiscountInput("");
    setDiscountError("");
  };

  const upgrade = async (plan, codeOverride = null) => {
    // Native builds (iOS App Store / Google Play) must route paid upgrades
    // to the web — Apple/Google forbid taking external payments inside the
    // app shell. The system browser opens keepsoul.app/plans so the user
    // completes the purchase there and comes back signed in.
    if (isNative()) {
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: `${WEB_BILLING_URL}?plan=${encodeURIComponent(plan)}` });
      } catch (_) {
        window.location.href = `${WEB_BILLING_URL}?plan=${encodeURIComponent(plan)}`;
      }
      return;
    }
    // Public visitors: redirect to signup before payment
    if (!user) {
      navigate(`/login?signup=1&plan=${encodeURIComponent(plan)}`);
      return;
    }
    setBusy(plan);
    try {
      const origin = window.location.origin;
      const payload = { plan, origin_url: origin };
      // Attach discount code if user introduced one. The codeOverride path
      // is used by the auto-claim flow (Iter 26) so we can't lose the code
      // to a React state-update race after applyDiscount().
      const code = codeOverride || discountApplied?.code;
      if (code) {
        payload.discount_code = code;
      }
      const r = await api.post("/payments/checkout", payload);
      // 100% discount path → server returns plan_applied=true, no Stripe
      if (r.data.free && r.data.plan_applied) {
        await refresh();
        toast.success(t("discount_applied_full") || "¡Plan activado al 100%!");
        // Hard reload to /dashboard to bypass any cached "free" plan state
        // anywhere in the React tree (sidebar plan pill, useAuth cache, etc.)
        setTimeout(() => {
          window.location.href = "/dashboard?payment=success";
        }, 600);
        return;
      }
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No URL");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg = "No se pudo iniciar el pago.";
      if (status === 401) msg = "Sesión expirada. Inicia sesión de nuevo.";
      else if (status === 503 || !err?.response) msg = "El servicio de pagos no responde. Inténtalo en unos minutos o contacta a soporte: support@keepsoul.app";
      else if (detail) msg = typeof detail === "string" ? detail : "Error al iniciar el pago.";
      toast.error(msg, { duration: 6000 });
      setBusy(null);
    }
  };

  const personalKey = "personal_annual"; // Premium is annual-only post Feb 2026
  const isCurrent = (k) => {
    if (k === personalKey) return user?.plan === "personal" || user?.plan === "personal_annual";
    if (k === "lifetime") return user?.plan === "lifetime";
    if (k === "free") return user?.plan === "free" || !user?.plan;
    return false;
  };

  const isPaid = user?.plan === "personal" || user?.plan === "personal_annual";
  const isLifetime = user?.plan === "lifetime";
  const isFree = !user?.plan || user?.plan === "free";
  const personalBtnLabel = () => {
    if (isCurrent(personalKey)) return t("pricing_current");
    if (isLifetime) return t("pricing_top_tier_owned");
    if (isPaid) return t("pricing_change_via_support");
    if (busy === personalKey) return <Loader2 className="w-4 h-4 animate-spin" />;
    return t("pricing_cta_start_personal");
  };
  const lifetimeBtnLabel = () => {
    if (isCurrent("lifetime")) return t("pricing_current");
    if (isLifetime) return t("pricing_lifetime_permanent");
    if (busy === "lifetime") return <Loader2 className="w-4 h-4 animate-spin" />;
    return t("pricing_cta_claim_lifetime");
  };

  return (
    <div data-testid="plans-page">
      <button
        onClick={() => (user ? navigate(-1) : navigate("/"))}
        className="text-muted hover:text-gold-light flex items-center gap-2 mb-6 text-sm"
        data-testid="plans-back"
      >
        <ArrowLeft className="w-4 h-4" /> {user ? t("cancel") : t("nav_back_home") || "Inicio"}
      </button>
      <PaymentSuccessBanner />

      {isWelcomeMode && (
        <div
          className="max-w-3xl mx-auto mb-6 rounded-md border border-emerald-500/40 px-5 py-4 text-center"
          style={{ background: "rgba(16,185,129,0.08)" }}
          data-testid="plans-welcome-banner"
        >
          <h2 className="font-display text-xl sm:text-2xl text-emerald-200 mb-1">
            ✓ {t("plans_welcome_title")}
          </h2>
          <p className="text-emerald-100/80 text-sm">{t("plans_welcome_sub")}</p>
        </div>
      )}

      <PageHeader title={t("plans_title")} subtitle={t("plans_sub")} />

      {isNative() && (
        <div
          className="max-w-3xl mx-auto mb-6 rounded-md border border-gold-subtle px-5 py-3 text-center text-xs sm:text-sm text-muted"
          style={{ background: "rgba(201,163,106,0.05)" }}
          data-testid="plans-native-billing-notice"
        >
          {t("plans_native_billing_notice")}
        </div>
      )}

      {/* Plans grid: Free / Premium / Lifetime (Lifetime hides when sold out) */}
      <div className={`grid grid-cols-1 ${slots.is_available ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-5 max-w-5xl mx-auto items-stretch`}>
        {/* FREE */}
        <div
          className={`glass-card rounded-md p-6 flex flex-col relative border border-gold-subtle ${
            isCurrent("free") ? "ring-1 ring-gold-subtle" : ""
          }`}
          data-testid="plan-card-free"
        >
          <h3 className="font-display text-2xl text-gold-light mb-1">{t("plan_free")}</h3>
          <p className="text-faded text-xs mb-4">{t("plan_free_sub")}</p>
          <div className="mb-1">
            <span className="font-display text-4xl text-gold-light">{symbol}0</span>
            <span className="text-faded text-sm"> {t("pricing_per_month_short")}</span>
          </div>
          <p className="text-[11px] text-faded/70 mb-5">{t("plan_free_billed_never")}</p>
          <ul className="space-y-2 mb-6 text-sm text-muted flex-1">
            {[
              { ok: true,  k: "plan_free_feat_vault" },         // 5 vault items
              { ok: true,  k: "plan_free_feat_pol" },           // Fe de Vida full
              { ok: true,  k: "plan_free_feat_heir" },          // 1 heir
              { ok: true,  k: "plan_free_feat_contact" },       // 1 contact
              { ok: true,  k: "plan_free_feat_assets" },        // 3 assets
              { ok: true,  k: "plan_free_feat_storage" },       // 1 GB
              { ok: false, k: "plan_free_feat_messages_blocked" },
              { ok: false, k: "plan_free_feat_ice_blocked" },
              { ok: false, k: "plan_free_feat_will_blocked" },
            ].map((row) => (
              <li key={row.k} className="flex items-start gap-2">
                {row.ok ? (
                  <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                ) : (
                  <Lock className="w-4 h-4 text-faded/60 mt-0.5 shrink-0" />
                )}
                <span className={row.ok ? "" : "text-faded/70 line-through decoration-faded/40"}>
                  {t(row.k)}
                </span>
              </li>
            ))}
          </ul>
          <button
            disabled
            className="btn-ghost w-full justify-center opacity-60 cursor-default"
            data-testid="plan-cta-free"
          >
            {isCurrent("free") ? t("pricing_current") : t("plan_free_cta_default")}
          </button>
        </div>

        {/* PERSONAL_ANNUAL — Premium 39€/año · MÁS POPULAR */}
        <div
          className={`glass-card rounded-md p-6 flex flex-col relative border-2 border-gold gold-glow ${
            isCurrent(personalKey) ? "ring-1 ring-gold" : ""
          }`}
          data-testid="plan-card-personal"
        >
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest bg-gold text-obsidian px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
            {t("plans_popular")}
          </span>
          <h3 className="font-display text-2xl text-gold-light mb-1">{t("plan_personal")}</h3>
          <p className="text-faded text-xs mb-4">{t("pricing_personal_sub")}</p>
          <div className="mb-1">
            <span className="font-display text-4xl text-gold-light">{symbol}39</span>
            <span className="text-faded text-sm">{t("pricing_per_year")}</span>
          </div>
          <p className="text-[11px] text-faded/70 mb-1">{t("pricing_billed_annually")}</p>
          <p className="text-xs text-gold-light/90 mb-5" data-testid="plan-personal-equiv">
            ≈ {symbol}3,25 {t("pricing_per_month_short")} · {t("plans_cancel_anytime")}
          </p>
          <ul className="space-y-2 mb-6 text-sm text-muted flex-1">
            {getPlanFeatureKeys("premium").map((k) => (
              <li key={k} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {t(k)}
              </li>
            ))}
          </ul>
          <button
            disabled={isCurrent(personalKey) || isLifetime || isPaid || busy === personalKey}
            onClick={() => upgrade(personalKey)}
            className="btn-gold w-full justify-center disabled:opacity-50"
            data-testid="plan-cta-personal"
          >
            {personalBtnLabel()}
          </button>
        </div>

        {/* LIFETIME — hidden completely when sold out */}
        {slots.is_available && (
          <div
            className="glass-card rounded-md p-6 flex flex-col relative border border-gold-subtle"
            data-testid="plan-card-lifetime"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest bg-red-500 text-white px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
              <Flame className="w-3 h-3" /> {t("pricing_lifetime_founders_badge")}
            </span>
            <h3 className="font-display text-2xl text-gold-light mb-1">{t("pricing_lifetime_label")}</h3>
            <p className="text-faded text-xs mb-4">{t("plans_lifetime_payment_once")}</p>
            <div className="mb-5">
              <span className="font-display text-4xl text-gold-light">{symbol}149</span>
            </div>
            <p className="text-xs text-gold mb-5" data-testid="lifetime-slots">
              {t("plans_lifetime_slots_avail").replace("{N}", slots.available).replace("{TOTAL}", slots.total)}
            </p>
            <ul className="space-y-2 mb-6 text-sm text-muted flex-1">
              {getPlanFeatureKeys("lifetime").map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {t(k)}
                </li>
              ))}
            </ul>
            <button
              disabled={isCurrent("lifetime") || isLifetime || busy === "lifetime"}
              onClick={() => upgrade("lifetime")}
              className="btn-ghost w-full justify-center disabled:opacity-50"
              data-testid="plan-cta-lifetime"
            >
              {lifetimeBtnLabel()}
            </button>
          </div>
        )}
      </div>

      {/* Bridge text — narrow, centered, between Lifetime and Founders */}
      {SHOW_FOUNDERS_PROGRAM && (
        <p
          className="text-center mx-auto max-w-3xl"
          style={{
            fontSize: "12px",
            letterSpacing: "0.05em",
            color: "rgba(201,169,110,0.6)",
            padding: "16px",
            marginTop: "24px",
          }}
          data-testid="plans-bridge-text"
        >
          {t("plans_bridge_text")}
        </p>
      )}

      {/* PROGRAMA FUNDADORES — premium €390 card */}
      {SHOW_FOUNDERS_PROGRAM && (
      <div
        id="founders-plan"
        className="glass-card rounded-md p-6 sm:p-7 mt-2 max-w-3xl mx-auto relative border-2 border-gold gold-glow"
        style={{ boxShadow: "0 0 80px rgba(201,163,106,0.18)" }}
        data-testid="plan-card-founders"
      >
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-obsidian px-3 py-1 rounded-full shadow-lg whitespace-nowrap"
          style={{ background: "#B8934A" }}
        >
          🏛️ {t("plan_founders_badge")}
        </span>
        <div className="text-center mb-4">
          <h3 className="font-display text-3xl text-gold-light mb-1">{t("plan_founders_title")}</h3>
          <p className="text-faded text-sm italic font-display">{t("plan_founders_subtitle")}</p>
        </div>
        <div className="text-center mb-1">
          <span className="font-display text-5xl text-gold-light">{symbol}390</span>
          <span className="text-faded text-sm ml-2">{t("pricing_lifetime_once")}</span>
        </div>
        <p className="text-center text-[11px] text-faded/70 mb-2">{t("pricing_billed_once")}</p>
        <p className="text-center text-xs text-gold mb-5" data-testid="founders-slots">
          🔥 {t("plan_founders_slots").replace("{N}", foundersSlots.available).replace("{TOTAL}", foundersSlots.total)}
        </p>

        <div className="font-label text-faded text-center mb-3 mt-2">
          {t("plan_founders_separator")}
        </div>
        <ul className="space-y-2 mb-6 text-sm text-muted">
          {[
            t("plan_founders_feat_1"),
            t("plan_founders_feat_2"),
            t("plan_founders_feat_3"),
            t("plan_founders_feat_4"),
            t("plan_founders_feat_5"),
            t("plan_founders_feat_6"),
            t("plan_founders_feat_7"),
            t("plan_founders_feat_8"),
            t("plan_founders_feat_9"),
          ].map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <details
          className="rounded-md mb-4"
          style={{
            background: "rgba(201,169,110,0.04)",
            border: "1px solid rgba(201,169,110,0.15)",
            padding: "20px",
          }}
          data-testid="founders-ownership-toggle"
        >
          <summary className="cursor-pointer text-gold-light font-display text-lg list-none flex items-center justify-between">
            <span>{t("plan_founders_ownership_title")}</span>
            <span className="text-gold text-xl">›</span>
          </summary>
          <div className="mt-4 text-sm text-muted whitespace-pre-line leading-relaxed">
            {t("plan_founders_ownership_body")}
          </div>
        </details>

        <div className="text-center mb-4">
          <a
            href="/founders-program"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gold hover:text-gold-light underline-offset-2 hover:underline"
            data-testid="founders-legal-link"
          >
            {t("plan_founders_legal_link")}
          </a>
        </div>

        <button
          disabled={isCurrent("founders") || busy === "founders"}
          onClick={() => upgrade("founders")}
          className="btn-gold w-full justify-center disabled:opacity-50"
          data-testid="plan-cta-founders"
        >
          {isCurrent("founders") ? t("plan_current") : t("plan_founders_cta")}
        </button>
        <p className="text-center mt-3" style={{ fontSize: "11px", color: "rgba(201,169,110,0.65)" }}>
          {t("plan_founders_trust")}
        </p>
      </div>
      )}

      <p className="text-center text-xs text-faded mt-8">
        {t("pricing_legal")}
      </p>

      {/* Discount code — prominent at the bottom of the plans grid */}
      <div className="max-w-md mx-auto mt-10 mb-4" data-testid="plans-discount-section">
        {discountApplied ? (
          <div className="glass-card rounded-md p-4 flex items-center gap-3 border-emerald-500/30 bg-emerald-500/5">
            <Tag className="w-4 h-4 text-emerald-400" />
            <div className="flex-1 text-sm">
              <span className="text-emerald-300">{t("discount_applied")}</span>{" "}
              <strong className="text-emerald-100">{discountApplied.code}</strong>{" "}
              <span className="text-emerald-300">— {discountApplied.percent}%</span>
              {discountApplied.is_full && (
                <span className="block text-xs text-emerald-200 mt-1">{t("discount_full_free")}</span>
              )}
            </div>
            <button
              onClick={clearDiscount}
              className="text-emerald-300 hover:text-emerald-100"
              aria-label="X"
              data-testid="plans-discount-clear"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <details
            className="glass-card rounded-md border-gold-subtle"
            open={isWelcomeMode}
            data-testid="plans-discount-toggle"
          >
            <summary
              className="cursor-pointer px-5 py-4 text-sm sm:text-base text-gold-light hover:text-gold flex items-center justify-center gap-2 select-none font-medium"
              data-testid="plans-discount-summary"
            >
              <Tag className="w-4 h-4 text-gold" /> {t("plans_discount_section_title")}
            </summary>
            <div className="px-4 pb-4 pt-2 flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                placeholder={t("plans_discount_placeholder")}
                className="input-field flex-1 uppercase"
                data-testid="plans-discount-input"
              />
              <button
                onClick={() => applyDiscount()}
                disabled={validating || !discountInput.trim()}
                className="btn-gold text-xs whitespace-nowrap"
                data-testid="plans-discount-apply"
              >
                {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {validating ? t("discount_validating") : t("plans_discount_apply")}
              </button>
            </div>
            {discountError && (
              <p className="px-4 pb-1 text-xs text-red-400" data-testid="plans-discount-error">
                {discountError}
              </p>
            )}
            <p className="px-4 pb-3 text-[11px] text-faded">{t("discount_will_apply")}</p>
          </details>
        )}
      </div>

      {/* Welcome mode — Skip for now button (only when user landed from confirm-email flow) */}
      {isWelcomeMode && user && (
        <div className="max-w-md mx-auto mb-8 text-center">
          <button
            onClick={() => { fireWelcomeOffer(false); navigate("/dashboard"); }}
            className="text-sm text-faded hover:text-gold-light underline-offset-2 hover:underline"
            data-testid="plans-skip-for-now"
          >
            {t("plans_skip_for_now")} →
          </button>
        </div>
      )}
    </div>
  );
}

export default function Plans() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }
  if (user) {
    // Logged-in users: render inside the authenticated dashboard shell (sidebar)
    return (
      <DashboardLayout>
        <PlansContent />
      </DashboardLayout>
    );
  }
  // Visitors: render in a public marketing shell
  return (
    <PublicPlansShell>
      <PlansContent />
    </PublicPlansShell>
  );
}
